import { utc } from '@date-fns/utc';
import { format, isValid, parseISO } from 'date-fns';
import type {
  AnswerWidgetCellValue,
  AnswerWidgetColumn,
  AnswerWidgetGranularity,
  AnswerWidgetMetricType,
} from './answerWidgetPayload';

const TICK_STEPS = [1, 2, 5, 10];
const AXIS_INTERVALS = 4;

const volumeFormat = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const axisTickFormat = new Intl.NumberFormat('en-US', { notation: 'compact' });

export function formatMetricValue(value: number, metricType: AnswerWidgetMetricType): string {
  if (metricType === 'volume') return volumeFormat.format(value);
  if (metricType === 'rate') return `${decimalFormat.format(value)}%`;
  return decimalFormat.format(value);
}

function formatTimeValue(value: AnswerWidgetCellValue, pattern: string): string {
  if (value === null) return '';

  const raw = String(value);
  const date = parseISO(raw, { in: utc });
  if (!isValid(date)) return raw;

  return format(date, pattern);
}

export function formatCategoryValue(value: AnswerWidgetCellValue, granularity: AnswerWidgetGranularity): string {
  return formatTimeValue(value, granularity === 'hour' ? 'h:mm a' : 'MMM d');
}

export function formatTimeLabel(value: AnswerWidgetCellValue, granularity: AnswerWidgetGranularity): string {
  return formatTimeValue(value, granularity === 'hour' ? 'MMM d, h:mm a' : 'MMM d');
}

/** The hovered hour in the chart tooltip, marked as UTC since every widget time is read as UTC. */
export function formatHourTooltipLabel(value: AnswerWidgetCellValue): string {
  return formatTimeValue(value, "MMM d, h:mm a 'UTC'");
}

export function formatTableCell(
  value: AnswerWidgetCellValue,
  column: AnswerWidgetColumn,
  granularity: AnswerWidgetGranularity | undefined,
  isTimeColumn: boolean
): string {
  if (value === null) return '';
  if (isTimeColumn && granularity !== undefined) return formatTimeLabel(value, granularity);
  if (typeof value !== 'number') return value;

  return column.metricType === undefined ? value.toLocaleString('en-US') : formatMetricValue(value, column.metricType);
}

export function formatAxisTick(value: number): string {
  return axisTickFormat.format(value);
}

export function buildAxisTicks(values: number[]): number[] {
  const finite = values.filter(value => Number.isFinite(value));
  const max = Math.max(0, ...finite);
  const min = Math.min(0, ...finite);
  const span = max - min;

  if (span === 0) return [0, 1];

  const interval = span / AXIS_INTERVALS;
  const magnitude = 10 ** Math.floor(Math.log10(interval));
  const step = (TICK_STEPS.find(multiplier => multiplier * magnitude >= interval) ?? 10) * magnitude;
  const first = Math.floor(min / step);
  const last = Math.ceil(max / step);

  return Array.from({ length: last - first + 1 }, (_, index) => (first + index) * step);
}

export function alignTickCounts(left: number[], right: number[]): [number[], number[]] {
  const length = Math.max(left.length, right.length);

  return [extendTicks(left, length), extendTicks(right, length)];
}

function extendTicks(ticks: number[], length: number): number[] {
  if (ticks.length >= length) return ticks;

  const step = ticks[1] - ticks[0];
  const top = ticks[ticks.length - 1];

  return [...ticks, ...Array.from({ length: length - ticks.length }, (_, index) => top + (index + 1) * step)];
}
